import React from 'react';
import {
  Box,
  Typography,
  Chip,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { VerifiedUser as VerifiedUserIcon, Edit as EditIcon } from '@mui/icons-material';
import { MaintenanceStatus, MaintenanceRecord } from '../../../../../types/api';
import { AttachmentList } from './AttachmentList';
import { useTranslation } from '../../../../../i18n';
import { prettifyMaintenanceType } from '../../../../../utils/maintenanceTypeUtils';

interface MaintenanceRecordFormProps {
  record: MaintenanceRecord;
  editingMaintenanceId: string | null;
  editingStartDate: string;
  editingEndDate: string;
  editingContent: string;
  loading: boolean;
  currentUserId?: string;
  canEdit?: boolean;
  onEditMaintenance: (
    recordId: string,
    currentStartDate: string,
    currentEndDate: string,
    currentContent?: string
  ) => void;
  onSaveMaintenanceEdit: (recordId: string) => void;
  onCancelEdit: () => void;
  onQualityCheckedToggle: (recordId: string, checked: boolean) => void;
  onStatusToggle: (recordId: string, status: MaintenanceStatus) => void;
  onEditingStartDateChange: (date: string) => void;
  onEditingEndDateChange: (date: string) => void;
  onEditingContentChange: (content: string) => void;
}

export const MaintenanceRecordForm: React.FC<MaintenanceRecordFormProps> = ({
  record,
  editingMaintenanceId,
  editingStartDate,
  editingEndDate,
  editingContent,
  loading,
  currentUserId: _currentUserId,
  canEdit = true,
  onEditMaintenance,
  onSaveMaintenanceEdit,
  onCancelEdit,
  onQualityCheckedToggle,
  onStatusToggle,
  onEditingStartDateChange,
  onEditingEndDateChange,
  onEditingContentChange,
}) => {
  const { t } = useTranslation();

  const renderMaintenanceRecord = (
    record: MaintenanceRecord,
    backgroundColor: string,
    borderColor: string
  ) => (
    <Box
      key={record.id}
      mb={2}
      p={2}
      sx={{ backgroundColor, borderRadius: 1, border: `1px solid ${borderColor}` }}
    >
      {/* Top row: work description on left, controls on right */}
      <Box display="flex" gap={2} mb={1} alignItems="flex-start">
        <Box flex={1}>
          <Typography variant="body1">
            {record.work_description || t('maintenanceTab.maintenancePerformed')}
          </Typography>
        </Box>
        <Box display="flex" gap={1} alignItems="center">
          {record.quality_checked && (
            <Chip
              icon={<VerifiedUserIcon />}
              label={t('maintenanceTab.qualityChecked')}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {canEdit && (
            <IconButton
              size="small"
              onClick={() =>
                onEditMaintenance(
                  record.id,
                  record.start_date,
                  record.end_date,
                  record.work_description
                )
              }
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
      </Box>
      <AttachmentList
        attachments={
          (record.attachments as unknown as Array<{
            name: string;
            url: string;
            key?: string;
            type?: string;
          }>) || []
        }
        compact
      />
      <Typography variant="caption" color="text.secondary">
        {t('maintenanceTab.startDate')} {record.start_date}
        <br />
        {t('maintenanceTab.endDate')} {record.end_date}
      </Typography>

      {/* User information */}
      <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        <Typography variant="caption" color="text.secondary">
          {t('maintenanceTab.performedBy')}{' '}
          {record.performed_by_name || t('maintenanceTab.unknownUser')}
        </Typography>
        {record.quality_checked && record.quality_checked_by_name && (
          <Typography variant="caption" color="text.secondary">
            {t('maintenanceTab.qualityCheckedBy')} {record.quality_checked_by_name}
          </Typography>
        )}
      </Box>

      {/* Status and Quality toggles - only shown to users who can edit */}
      {canEdit && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          {/* Only show quality check toggle when status is completed */}
          {record.status === 'completed' && (
            <FormControlLabel
              control={
                <Switch
                  checked={record.quality_checked || false}
                  onChange={(e) => onQualityCheckedToggle(record.id, e.target.checked)}
                  disabled={false}
                  size="small"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VerifiedUserIcon sx={{ fontSize: '16px' }} />
                  <Typography variant="caption">{t('maintenanceTab.qualityChecked')}</Typography>
                </Box>
              }
            />
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={record.status}
              label="Status"
              onChange={(e) => onStatusToggle(record.id, e.target.value as MaintenanceStatus)}
            >
              <MenuItem value="planned">Planned</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Box>
      )}

      {/* Edit form - appears when this record is being edited */}
      {editingMaintenanceId === record.id &&
        (() => {
          const contentEmpty = editingContent.trim() === '';
          const startEmpty = !editingStartDate;
          const endEmpty = !editingEndDate;
          const saveDisabled = loading || contentEmpty || startEmpty || endEmpty;
          return (
            <Box mt={2} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Box display="flex" gap={1} alignItems="flex-start" mb={1}>
                <TextField
                  size="small"
                  type="date"
                  value={editingStartDate}
                  onChange={(e) => onEditingStartDateChange(e.target.value)}
                  sx={{ width: 150 }}
                  label={t('maintenanceTab.startDateLabel')}
                  required
                  error={startEmpty}
                />
                <TextField
                  size="small"
                  type="date"
                  value={editingEndDate}
                  onChange={(e) => onEditingEndDateChange(e.target.value)}
                  sx={{ width: 150 }}
                  label={t('maintenanceTab.endDateLabel')}
                  inputProps={{ min: editingStartDate || undefined }}
                  required
                  error={endEmpty}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => onSaveMaintenanceEdit(record.id)}
                  disabled={saveDisabled}
                >
                  {t('common.save')}
                </Button>
                <Button size="small" onClick={onCancelEdit} disabled={loading}>
                  {t('common.cancel')}
                </Button>
              </Box>
              <TextField
                size="small"
                multiline
                rows={2}
                fullWidth
                label={t('maintenanceTab.workDescription')}
                value={editingContent}
                onChange={(e) => onEditingContentChange(e.target.value)}
                required
                error={contentEmpty}
                helperText={contentEmpty ? 'Work description is required' : ' '}
              />
            </Box>
          );
        })()}
    </Box>
  );

  return (
    <Box mb={2}>
      <Typography variant="h6" gutterBottom>
        {prettifyMaintenanceType(record.maintenance_type)}
      </Typography>
      {renderMaintenanceRecord(record, '#fff8e1', '#ff9800')}
    </Box>
  );
};
